import type { jsPDF } from "jspdf";
import type { Address, PartyInfo } from "@/domain/types";

export const PDF_COLORS = {
  charcoal: [23, 35, 41] as [number, number, number],
  charcoal2: [35, 51, 58] as [number, number, number],
  mint: [42, 160, 135] as [number, number, number],
  mintPale: [232, 246, 241] as [number, number, number],
  gold: [207, 145, 43] as [number, number, number],
  coral: [222, 91, 80] as [number, number, number],
  ink: [36, 50, 56] as [number, number, number],
  muted: [104, 119, 125] as [number, number, number],
  border: [220, 228, 224] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

export function addressLines(address: Address): string[] {
  const cityLine = [
    address.city,
    address.state,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(", ");

  return [
    address.line1,
    address.line2,
    cityLine,
    address.country,
  ].filter(Boolean);
}

export function partyLines(party: PartyInfo): string[] {
  return [
    party.name,
    party.company,
    party.email,
    party.phone,
    ...addressLines(party.address),
  ].filter(Boolean);
}

export function drawPdfBrandMark(
  doc: jsPDF,
  x: number,
  y: number,
  size = 13,
): void {
  const unit = size / 13;

  doc.setFillColor(...PDF_COLORS.charcoal2);
  doc.roundedRect(x, y, size, size, 2.7 * unit, 2.7 * unit, "F");

  doc.setFillColor(...PDF_COLORS.white);
  doc.roundedRect(
    x + 3.1 * unit,
    y + 2.2 * unit,
    7.4 * unit,
    8.7 * unit,
    0.9 * unit,
    0.9 * unit,
    "F",
  );

  doc.setFillColor(...PDF_COLORS.mint);
  doc.triangle(
    x + 8.3 * unit,
    y + 2.2 * unit,
    x + 10.5 * unit,
    y + 4.4 * unit,
    x + 8.3 * unit,
    y + 4.4 * unit,
    "F",
  );

  doc.setFillColor(...PDF_COLORS.gold);
  doc.triangle(
    x + 6.2 * unit,
    y + 3.5 * unit,
    x + 7.2 * unit,
    y + 6.2 * unit,
    x + 6.2 * unit,
    y + 8.9 * unit,
    "F",
  );
  doc.triangle(
    x + 3.5 * unit,
    y + 6.2 * unit,
    x + 6.2 * unit,
    y + 5.2 * unit,
    x + 8.9 * unit,
    y + 6.2 * unit,
    "F",
  );

  doc.setDrawColor(...PDF_COLORS.charcoal);
  doc.setLineWidth(0.45 * unit);
  doc.line(
    x + 7.4 * unit,
    y + 8.2 * unit,
    x + 9.3 * unit,
    y + 8.2 * unit,
  );
  doc.line(
    x + 7.4 * unit,
    y + 9.3 * unit,
    x + 9.3 * unit,
    y + 9.3 * unit,
  );
}

export function drawFooter(doc: jsPDF, label: string): void {
  const pageCount = doc.getNumberOfPages();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...PDF_COLORS.border);
    doc.line(16, 281, 194, 281);
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text(
      "Thank you for your business and continued trust.",
      16,
      287,
    );
    doc.text(
      `Created with Ledgerly by Mark · ${label} · Page ${page} of ${pageCount}`,
      194,
      287,
      { align: "right" },
    );
  }
}
